// Platform admin (/admin/fleet) — WHAT EACH PROJECT COSTS US AND WHAT IT PAYS.
//
// The margin view underneath the payment flow. Today it shows the half we can
// measure honestly; dollars land when Cost Explorer's tag data starts accruing
// (see the header note on the page and docs/OBSERVABILITY.md §5).
//
// Everything here comes from TWO sources the operator already reaches, so this
// page needs no new IAM, no server function and no bucket walk:
//
//   1. the PLATFORM bucket — the registry manifests (`s3db/shared/projects/`)
//      and the deploy-time usage inventory (`s3db/shared/usage/`);
//   2. CloudWatch's FREE daily S3 storage metrics, read straight from the
//      browser with the operator's own STS, exactly as /admin/observability
//      reads Lambda metrics.
//
// What is deliberately NOT here, and why: per-project bandwidth and request
// counts. Both are sold on the public pricing page and NEITHER is measurable
// yet — every non-custom-domain project shares one CloudFront distribution
// (untaggable per tenant, and access logs are off), and S3 request counts need
// server access logs. Showing a made-up number for a billed dimension would be
// worse than showing none, so the page states the gap instead.

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import type { StorageSeries } from "@s3p/sdk";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { s3pProject } from "/__s3p/project.js";
import { COMPAT_FLOOR, PLATFORM_REV, platformBucketName } from "/sdk/index.js";
import { useAsync } from "../../hooks/useAsync.ts";
import type { ProjectManifest } from "../../lib/contracts.ts";
import { projectPath } from "../../lib/hosts.ts";
import { useSession } from "../../session/SessionProvider.tsx";

/** The Stripe receipt the webhook records beside the entitlement it granted.
 *  Console-local (see apps/console/functions/lib/billing.ts) — the platform
 *  manifest contract stays payment-free. */
interface BillingReceipt {
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  /** Stripe subscription status at the last webhook (`active`, `past_due`, …). */
  status?: string;
  updatedAt?: string;
}

type FleetManifest = ProjectManifest & { billing?: BillingReceipt };

/** What `s3p deploy` reports about the code it shipped. */
interface UsageManifest {
  slug: string;
  sdkVersion?: string;
  cliVersion?: string;
  serverFunctions?: string[];
  at?: string;
}

/** The Free-tier storage allowance from the PUBLIC pricing page
 *  (apps/console/src/routes/Pricing.tsx). Duplicated as a number here on
 *  purpose: this page's job is to say who would cross it if we billed today,
 *  so it has to compare against the figure we actually advertise. Change both
 *  together — or make it one constant the day a real plan model exists. */
const FREE_STORAGE_GB = 5;

const GB = 1024 ** 3;

function fmtBytes(bytes: number | null): string {
  // `null` is "CloudWatch has no datapoint", NOT zero — a brand-new bucket and
  // an empty one must not read alike.
  if (bytes === null) return "—";
  if (bytes < GB / 100) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / GB).toFixed(2)} GB`;
}

function fmtCount(n: number | null): string {
  return n === null ? "—" : Math.round(n).toLocaleString();
}

function fmtDay(t?: string | null): string {
  return (t ?? "").slice(0, 10) || "—";
}

/** Compare dotted numeric versions. Returns true when `v` is BELOW `floor`.
 *  Unknown/unparseable versions are never reported as below the floor — an
 *  absent value means "this project hasn't deployed since usage reporting
 *  landed", which is not the same claim as "it is out of date". */
function belowFloor(v: string | undefined, floor: string): boolean {
  if (!v) return false;
  const parse = (s: string) => s.split(".").map((p) => Number.parseInt(p, 10) || 0);
  const a = parse(v);
  const b = parse(floor);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d < 0;
  }
  return false;
}

interface Row {
  manifest: FleetManifest;
  usage?: UsageManifest;
  storage?: StorageSeries;
}

interface FleetData {
  rows: Row[];
  /** Why storage is missing, when it is. `null` means the read SUCCEEDED — any
   *  "—" in the table is then genuinely "CloudWatch has no datapoint", not a
   *  permission problem wearing the same costume. */
  storageError: string | null;
}

export function Fleet() {
  const { client, me } = useSession();
  const accountId = s3pProject.aws.accountId;

  const fleet = useAsync<FleetData>(async () => {
    if (!client) return { rows: [], storageError: null };

    // Manifests and the usage inventory both live on the PLATFORM bucket, so
    // they are two ordinary collection reads with the operator's session.
    const [manifests, usage] = await Promise.all([
      client.collection<FleetManifest>("projects", { filename: "manifest.json" }).list(),
      client
        .collection<UsageManifest>("usage", { filename: "usage.json" })
        .list()
        .catch(() => [] as UsageManifest[]),
    ]);

    manifests.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
    const usageBySlug = new Map(usage.map((u) => [u.slug, u]));

    // Storage must not take the whole page down — the registry half is useful on
    // its own. But it must not fail SILENTLY either: reading the manifests only
    // needs the MEMBER floor, while GetMetricData needs `observe`, so a session
    // without it renders a complete-looking table whose every storage cell is
    // "—". That is the denied-vs-empty collapse this file warns about in
    // fmtBytes and then used to commit right here, by catching into `[]`.
    // Keep the error and say which it was.
    let storage: StorageSeries[] = [];
    let storageError: string | null = null;
    try {
      storage = await client.metrics.storage(
        manifests.map((m) => platformBucketName(accountId, m.slug)),
      );
    } catch (e) {
      storageError = e instanceof Error ? e.message : String(e);
    }
    const storageByBucket = new Map(storage.map((s) => [s.bucket, s]));

    return {
      storageError,
      rows: manifests.map((manifest) => ({
        manifest,
        usage: usageBySlug.get(manifest.slug),
        storage: storageByBucket.get(platformBucketName(accountId, manifest.slug)),
      })),
    };
  }, [client, accountId]);

  const rows = fleet.data?.rows ?? [];
  const storageError = fleet.data?.storageError ?? null;

  // Totals skip unknowns rather than counting them as zero, so "12.4 GB across
  // 8 of 11 projects" stays an honest sentence.
  const measured = rows.filter((r) => typeof r.storage?.bytes === "number");
  const totalBytes = measured.reduce((sum, r) => sum + (r.storage?.bytes ?? 0), 0);
  const paying = rows.filter((r) => r.manifest.entitlements?.customDomains === true).length;
  const behindRev = rows.filter(
    (r) =>
      typeof r.manifest.provisioner?.rev === "number" && r.manifest.provisioner.rev < PLATFORM_REV,
  ).length;
  const overFree = measured.filter((r) => (r.storage?.bytes ?? 0) > FREE_STORAGE_GB * GB).length;

  return (
    <Stack gap="xl" p="lg">
      <Stack gap={4}>
        <Title order={1}>Fleet</Title>
        <Text c="dimmed">
          What every project consumes and what it pays. Storage comes from CloudWatch's free daily
          S3 metrics; plan state comes from the project registry.{" "}
          <Anchor component={Link} to="/admin">
            Registry
          </Anchor>{" "}
          ·{" "}
          <Anchor component={Link} to="/admin/observability">
            Observability
          </Anchor>
        </Text>
      </Stack>

      <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
        <Text size="sm" fw={600}>
          Two of the three billed dimensions are not measured yet.
        </Text>
        <Text size="sm" mt={4}>
          The pricing page sells storage, requests and bandwidth. Only <b>storage</b> is measurable
          today. Per-project <b>bandwidth</b> needs CloudFront access logs (off — and every project
          without a custom domain shares one distribution, which carries no per-tenant tag), and{" "}
          <b>requests</b> need S3 server access logs. Neither backfills, so the numbers only start
          the day they are switched on. Dollar amounts arrive once the <Code>Project</Code> cost
          allocation tag is activated in Billing.
        </Text>
      </Alert>

      {fleet.error && (
        <Alert color="red" variant="light" title="Could not load the fleet">
          {fleet.error}
        </Alert>
      )}

      {storageError && (
        <Alert color="red" variant="light" title="Storage unavailable — not measured, not empty">
          <Text size="sm">
            Every storage cell below reads “—” because the CloudWatch call FAILED, not because the
            buckets are empty.
          </Text>
          <Text size="sm" mt="xs">
            Reading the project registry needs only the member floor, but{" "}
            <Code>cloudwatch:GetMetricData</Code> needs the <Code>observe</Code> capability, which
            is granted <b>account-wide on the root project only</b>. So an identity that is an
            operator of some other project still reaches this page — the admin gate matches any
            group containing “owner” or “admin” — and then cannot read metrics. Sign in with an
            identity holding <Code>console:owner</Code> or <Code>console:admin</Code>.
          </Text>
          <Text size="sm" mt="xs">
            This session: <Code>{me?.username ?? me?.sub ?? "unknown"}</Code> · role{" "}
            <Code>{me?.role ?? "none"}</Code>
            {me?.groups?.length ? (
              <>
                {" "}
                · groups <Code>{me.groups.join(", ")}</Code>
              </>
            ) : null}
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            {storageError}
          </Text>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Stat label="Projects" value={String(rows.length)} />
        <Stat
          label="Storage"
          // A failed read is NOT zero bytes. "0.0 MB measured on 0 of 11" reads
          // like a healthy empty fleet; it was the shape of an AccessDenied.
          value={storageError ? "—" : fmtBytes(totalBytes)}
          hint={
            storageError
              ? "unavailable — see above"
              : measured.length === rows.length
                ? undefined
                : `measured on ${measured.length} of ${rows.length}`
          }
          alert={!!storageError}
        />
        <Stat
          label="Over free tier"
          value={String(overFree)}
          hint={`storage above ${FREE_STORAGE_GB} GB`}
          alert={overFree > 0}
        />
        <Stat label="Paying" value={String(paying)} hint="custom domains" />
      </SimpleGrid>

      <Card withBorder radius="md" padding="lg">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <Title order={3}>Projects</Title>
            {fleet.loading && <Loader size="xs" />}
          </Group>
          <Group gap="xs">
            {behindRev > 0 && (
              <Badge color="orange" variant="light">
                {behindRev} behind rev {PLATFORM_REV}
              </Badge>
            )}
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} />}
              onClick={fleet.reload}
              loading={fleet.loading}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Project</Table.Th>
                <Table.Th>Plan</Table.Th>
                <Table.Th ta="right">Storage</Table.Th>
                <Table.Th ta="right">Objects</Table.Th>
                <Table.Th>Provisioner</Table.Th>
                <Table.Th>SDK</Table.Th>
                <Table.Th>Created</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <FleetRow key={r.manifest.slug} row={r} />
              ))}
              {!fleet.loading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" size="sm">
                      No projects visible to this session.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        <Text size="xs" c="dimmed" mt="sm">
          S3 publishes storage metrics once a day, so figures lag ~24–48h. “—” means CloudWatch has
          no datapoint yet, which is not the same as empty.
        </Text>
      </Card>
    </Stack>
  );
}

function FleetRow({ row }: { row: Row }) {
  const { manifest: m, usage, storage } = row;
  const rev = m.provisioner?.rev;
  const isBehind = typeof rev === "number" && rev < PLATFORM_REV;
  const sdk = usage?.sdkVersion;
  const isBelowFloor = belowFloor(sdk, COMPAT_FLOOR.minSdk);
  const overFree = typeof storage?.bytes === "number" && storage.bytes > FREE_STORAGE_GB * GB;

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Anchor component={Link} to={projectPath(m.slug)} fw={600}>
            {m.slug}
          </Anchor>
          {m.environment && (
            <Badge size="xs" variant="light" color="grape">
              {m.environment}
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          {m.name}
          {m.environmentOf ? ` · env of ${m.environmentOf}` : ""}
        </Text>
      </Table.Td>

      <Table.Td>
        <PlanCell manifest={m} />
      </Table.Td>

      <Table.Td ta="right">
        <Text size="sm" c={overFree ? "orange" : undefined} fw={overFree ? 600 : undefined}>
          {fmtBytes(storage?.bytes ?? null)}
        </Text>
        {storage?.at && (
          <Text size="xs" c="dimmed">
            {fmtDay(storage.at)}
          </Text>
        )}
      </Table.Td>

      <Table.Td ta="right">
        <Text size="sm">{fmtCount(storage?.objects ?? null)}</Text>
      </Table.Td>

      <Table.Td>
        {typeof rev === "number" ? (
          <Tooltip label={m.provisioner?.by ?? ""} disabled={!m.provisioner?.by}>
            <Badge color={isBehind ? "orange" : "gray"} variant="light">
              rev {rev}
              {isBehind ? ` → ${PLATFORM_REV}` : ""}
            </Badge>
          </Tooltip>
        ) : (
          <Text size="xs" c="dimmed">
            unstamped
          </Text>
        )}
      </Table.Td>

      <Table.Td>
        {sdk ? (
          <Badge color={isBelowFloor ? "red" : "gray"} variant="light">
            {sdk}
            {isBelowFloor ? " · below floor" : ""}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            not reported
          </Text>
        )}
      </Table.Td>

      <Table.Td>
        <Text size="xs" c="dimmed">
          {fmtDay(m.createdAt)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

/** Plan today is ONE boolean plus a Stripe status — there is no tier model yet
 *  (`ProjectEntitlements` has a single field, and the pricing page's "Pay as you
 *  go" has no Stripe product behind it). Rendering exactly that, rather than
 *  inventing a "Free/Pro" label, keeps the gap visible. */
function PlanCell({ manifest }: { manifest: FleetManifest }) {
  const entitled = manifest.entitlements?.customDomains === true;
  const status = manifest.billing?.status;

  if (!entitled && !status) {
    return (
      <Badge variant="light" color="gray">
        free
      </Badge>
    );
  }

  // A subscription that is past_due/unpaid still shows the entitlement, because
  // access is revoked on `customer.subscription.deleted`, not on dunning — the
  // colour is what surfaces the dunning.
  const bad = status && !["active", "trialing"].includes(status);
  return (
    <Group gap={6} wrap="nowrap">
      {entitled && (
        <Badge variant="light" color="green">
          custom domains
        </Badge>
      )}
      {status && (
        <Badge variant="light" color={bad ? "red" : "gray"}>
          {status}
        </Badge>
      )}
    </Group>
  );
}

function Stat({
  label,
  value,
  hint,
  alert,
}: {
  label: string;
  value: string;
  hint?: string;
  alert?: boolean;
}) {
  return (
    <Card withBorder radius="md" padding="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text fz={28} fw={700} c={alert ? "orange" : undefined}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Card>
  );
}
